import React, { FC } from 'react';
import { Button } from '@vadiun/react-components';
import { Field, Form, Formik } from 'formik';
import { TextField } from 'formik-material-ui';
import {
  Autocomplete,
  AutocompleteRenderInputParams
} from 'formik-material-ui-lab';
import { TextField as TextFieldMaterial } from '@material-ui/core';
import * as Yup from 'yup';
import { Person, Business } from '@material-ui/icons';
import { Company, UserFormValues } from '../../../models/appUserModel';
import { Role } from '../../../models/appUsersModels';
import ImageUserType from './ImageUserType';
import FormikAddress, {
  domicilioInitialValues,
  domicilioValidationOptional
} from 'shared/components/FormikAddress';
import InternationalPhones from 'shared/components/phone/InternationalPhones';
import { createPhoneValidationTest } from 'shared/components/phone/phoneValidation';

interface UserAppClientCreateProps {
  onSubmit: (values: UserFormValues) => void;
  companies: Company[];
  initialValues?: UserFormValues;
  isEditing?: boolean;
  onCancel?: () => void;
}

const baseValidationSchema = {
  fullname: Yup.string().min(3, 'Mínimo 3 caracteres'),
  first_name: Yup.string().min(3, 'Mínimo 3 caracteres'),
  last_name: Yup.string().min(3, 'Mínimo 3 caracteres'),
  email: Yup.string()
    .required('Email es requerido')
    .email('Ingrese un email válido'),
  phone: Yup.string().test(
    'phone-validation',
    'Número de teléfono inválido',
    createPhoneValidationTest('AR', true)
  ),
  alias: Yup.string().min(2, 'Mínimo 2 caracteres')
};

const validationSchema = Yup.lazy((values: UserFormValues) => {
  return Yup.object().shape({
    ...baseValidationSchema,
    fullname:
      values.role === Role.CORPORATE
        ? baseValidationSchema.fullname.required('Nombre completo es requerido')
        : baseValidationSchema.fullname,
    first_name:
      values.role === Role.PARTICULAR
        ? baseValidationSchema.first_name.required('Nombre es requerido')
        : baseValidationSchema.first_name,
    last_name:
      values.role === Role.PARTICULAR
        ? baseValidationSchema.last_name.required('Apellido es requerido')
        : baseValidationSchema.last_name,
    company:
      values.role === Role.CORPORATE
        ? Yup.mixed().required(
            'Empresa es requerida para usuarios corporativos'
          )
        : Yup.mixed().notRequired(),
    phone:
      values.role === Role.CORPORATE
        ? baseValidationSchema.phone.required('Teléfono es requerido')
        : baseValidationSchema.phone,
    address:
      values.role === Role.PARTICULAR
        ? domicilioValidationOptional
        : Yup.mixed().notRequired()
  });
});

const initialFormValues: UserFormValues = {
  fullname: '',
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  company: null,
  role: null,
  alias: '',
  address: domicilioInitialValues
};

const UserAppClientCreateEditForm: FC<UserAppClientCreateProps> = ({
  onSubmit,
  companies,
  initialValues = initialFormValues,
  isEditing = false,
  onCancel
}) => {
  const modifiedInitialValues = isEditing
    ? initialValues
    : { ...initialFormValues, role: null };

  const handleSubmit = async (
    values: UserFormValues,
    { setSubmitting }: any
  ) => {
    try {
      await onSubmit(values);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Formik
      initialValues={modifiedInitialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {({
        isSubmitting,
        isValid,
        dirty,
        touched,
        errors,
        submitCount,
        values,
        setFieldValue
      }) => (
        <Form
          className={`flex bg-white rounded-xl shadow-lg border border-gray-100 gap-4 p-6 transition-[width] 
    duration-500 ease-[cubic-bezier(0.4,0,0.2,1)] ${
      !values.role ? 'w-1/2' : 'w-full'
    }`}
          style={{ boxShadow: '0px 0px 10px rgb(79, 151, 190) ' }}
        >
          <div
            className="animate-header-slide flex w-full flex-col gap-4 "
            style={
              values.role !== null
                ? { borderRight: '4px solid #0270AB', marginRight: '8px' }
                : {}
            }
          >
            <div className="flex flex-col w-full items-center gap-4 ">
              <ImageUserType
                role={values.role}
                key={`person ${values.role}`}
                className="animate-form-expand"
              />
              <h2
                className="text-3xl font-bold text-center"
                style={{ color: '#0270AB', textShadow: '2px 2px 3px #61FFD0' }}
              >
                {isEditing ? 'Editar Usuario' : 'Nuevo Usuario'}
              </h2>
              <p
                className="flex w-auto justify-center text-gray-700 md:whitespace-nowrap pt-4"
                style={
                  values.role === null ? { borderTop: '2px solid #0270AB' } : {}
                }
              >
                {isEditing ? '' : 'Selecciona el tipo de usuario a crear:'}
              </p>
            </div>
            <div className="flex justify-around mb-4 p-4 rounded-lg">
              <label className="flex items-center gap-2">
                <Field
                  disabled={isEditing}
                  type="checkbox"
                  name="role"
                  value={Role.CORPORATE}
                  checked={values.role === Role.CORPORATE}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const isChecked = e.target.checked;
                    setFieldValue('role', isChecked ? Role.CORPORATE : null);
                    if (!isChecked) setFieldValue('company', null);
                  }}
                  className="form-checkbox h-5 w-5 text-blue-600"
                />
                <div>
                  <Person style={{ color: '#ff8700' }} />
                  <Business style={{ color: '#3a77ff' }} />
                </div>
                <span className="font-medium">Corporativo</span>
              </label>

              <label className="flex items-center gap-2">
                <Field
                  disabled={isEditing}
                  type="checkbox"
                  name="role"
                  value={Role.PARTICULAR}
                  checked={values.role === Role.PARTICULAR}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const isChecked = e.target.checked;
                    setFieldValue('role', isChecked ? Role.PARTICULAR : null);
                    if (!isChecked) {
                      setFieldValue('alias', '');
                      setFieldValue('address', '');
                      setFieldValue('photo', null);
                    }
                  }}
                  className="form-checkbox h-5 w-5 text-green-600"
                />
                <Person style={{ color: '#3a77ff' }} />
                <span className="font-medium">Particular</span>
              </label>
            </div>
          </div>
          {(values.role === Role.CORPORATE ||
            values.role === Role.PARTICULAR) && (
            <div
              key={`form ${values.role}`}
              className="animate-form-expand flex flex-col w-full justify-between"
            >
              <div className="flex justify-between ">
                <div className="container space-y-6 ">
                  <div className="flex justify-between gap-4">
                    {values.role === Role.CORPORATE && (
                      <Field
                        name="fullname"
                        label="Nombre completo"
                        variant="outlined"
                        component={TextField}
                        fullWidth
                      />
                    )}
                    {values.role === Role.PARTICULAR && (
                      <>
                        <Field
                          name="first_name"
                          label="Nombre"
                          variant="outlined"
                          component={TextField}
                          fullWidth
                        />
                        <Field
                          name="last_name"
                          label="Apellido"
                          variant="outlined"
                          component={TextField}
                          fullWidth
                        />
                      </>
                    )}

                    <Field
                      name="email"
                      label="Email"
                      type="email"
                      variant="outlined"
                      component={TextField}
                      fullWidth
                    />
                  </div>
                  <div className="flex justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <InternationalPhones
                        name="phone"
                        label="Teléfono"
                        variant="floating"
                      />
                    </div>
                    {values.role === Role.CORPORATE && (
                      <Field
                        className="flex-1"
                        disabled={isEditing}
                        name="company"
                        component={Autocomplete}
                        options={companies}
                        getOptionLabel={(option: Company) => option.name}
                        renderInput={(
                          params: AutocompleteRenderInputParams
                        ) => (
                          <TextFieldMaterial
                            {...params}
                            error={touched['company'] && !!errors['company']}
                            helperText={errors['company']}
                            label="Empresa"
                            variant="outlined"
                            fullWidth
                          />
                        )}
                      />
                    )}

                    {values.role === Role.PARTICULAR && (
                      <Field
                        className="flex-1"
                        name="alias"
                        label="Alias"
                        variant="outlined"
                        component={TextField}
                      />
                    )}
                  </div>

                  {values.role === Role.PARTICULAR && (
                    <Field
                      className="col-span-6"
                      name="address"
                      variant="outlined"
                      component={FormikAddress}
                      fullWidth
                    />
                  )}
                </div>
              </div>

              <div className="flex justify-between  pt-4">
                {onCancel && (
                  <Button
                    type="button"
                    variant="light"
                    color="gray"
                    onClick={onCancel}
                    disabled={isSubmitting}
                  >
                    Cancelar
                  </Button>
                )}

                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={isSubmitting || !dirty}
                >
                  {isEditing ? 'Actualizar Usuario' : 'Crear Usuario'}
                </Button>
              </div>
            </div>
          )}
        </Form>
      )}
    </Formik>
  );
};

export default UserAppClientCreateEditForm;
